import React, { Component, ReactNode, ErrorInfo } from 'react';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): State {
    return { hasError: true, error: null, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });

    // Log to console with full details
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo?.componentStack);
    console.groupEnd();

    // TODO: Send to error reporting service (e.g., Sentry)
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    // }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      const isDevelopment = (import.meta as any).env?.MODE === 'development';

      return (
        <Box p={3}>
          <Alert
            severity="error"
            style={{
              marginBottom: '1.5rem',
              backgroundColor: '#fdeded',
              border: '1px solid #f5c6cb',
            }}
          >
            <AlertTitle style={{ fontWeight: 600, fontSize: '1.1rem' }}>Something went wrong</AlertTitle>
            {isDevelopment && error?.message ? (
              <Typography
                variant="body2"
                component="div"
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.875rem',
                  color: '#d32f2f',
                }}
              >
                {error.message}
              </Typography>
            ) : (
              <Typography variant="body2" style={{ marginTop: '0.5rem' }}>
                An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
              </Typography>
            )}
          </Alert>

          <Box display="flex" mb={3} style={{ gap: '1rem' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReset}
              style={{ textTransform: 'none', fontWeight: 500 }}
            >
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()} style={{ textTransform: 'none' }}>
              Reload Page
            </Button>
          </Box>

          {isDevelopment && errorInfo?.componentStack && (
            <Paper
              elevation={2}
              style={{
                padding: '1rem',
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{
                  fontWeight: 600,
                  color: '#666',
                  marginBottom: '0.75rem',
                }}
              >
                🔍 Component Stack (Development Only)
              </Typography>
              <pre
                style={{
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#424242',
                  lineHeight: 1.5,
                }}
              >
                {errorInfo.componentStack}
              </pre>
            </Paper>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}
